import { gql } from '@apollo/client';
import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
} from '@apollo/orbit';
import {
  FormikComputedProps,
  FormikHandlers,
  FormikHelpers,
  FormikState,
} from 'formik';
import { printSchema } from 'graphql';
import React, { useEffect, useMemo } from 'react';

import { getIntrospectionHelper } from 'src/app/account/supergraphListPage/newSupergraphOnboarding/introspectionHelper/getIntrospectionHelper';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { URLField } from 'src/components/urlField/URLField';
import { API_NAME_MAX_CHAR } from 'src/lib/graphOnboarding/constants';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isLocalUrl } from 'src/lib/isLocalUrl';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { InputCharCount } from '../../../../components/InputCharCount';
import { HeaderEntry } from '../../explorerPage/hooks/useExplorerState/useHeadersManagerContext/shared';
import { headersObjectFromDefinitions } from '../../explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { useSchemaFromEndpointIntrospection } from '../../explorerPage/hooks/useExplorerState/useSchema/useSchemaFromEndpointIntrospection/useSchemaFromEndpointIntrospection';
import { AddSubgraphFormFields } from '../../graphSettingsPage/variantsPage/addVariantWizard/shared';
import { CompositionErrorsBanner } from '../../subgraphsLayout/subgraphsPage/addSubgraphModal/CompositionErrorsBanner';

import { AdvancedOptionsSection } from './AdvancedOptionsSection';

// formik's onSubmit should call this mutation
export const AddSubgraphFormMutation = gql`
  mutation AddSubgraphFormMutation(
    $graphId: ID!
    $variant: String!
    $subgraphName: String!
    $routingUrl: String!
    $revision: String!
    $partialSchema: PartialSchemaInput!
  ) {
    service(id: $graphId) {
      publishSubgraph(
        graphVariant: $variant
        name: $subgraphName
        url: $routingUrl
        revision: $revision
        activePartialSchema: $partialSchema
      ) {
        graphCompositionID
        updatedGateway
        errors {
          message
          locations {
            column
            line
          }
          code
        }
      }
    }
  }
`;

export const ADD_PRIVATE_SUBGRAPHS_QUERY = gql`
  query AddPrivateSubgraphsQuery(
    $accountId: ID!
    $cloudProvider: CloudProvider!
  ) {
    account(id: $accountId) {
      id
      privateSubgraphs(cloudProvider: $cloudProvider) {
        name
        cloudProvider
        region {
          name
          code
          provider
          state
          country
        }
        domainUrl
        status
      }
    }
  }
`;

export interface AddSubgraphFormProps<T extends AddSubgraphFormFields> {
  graphRef: GraphRef;
  isSubmitting: boolean;
  checkSchemaData?:
    | GraphQLTypes.AddSubgraphCheckSchemaMutation
    | null
    | undefined;
  subgraphNameAvailable: boolean;
  shouldShowCompositionErrorsBanner: boolean;
  urlPlaceholder: string;
  // formik props
  handleBlur: FormikHandlers['handleBlur'];
  touched: FormikState<T>['touched'];
  setValues: FormikHelpers<T>['setValues'];
  handleSubmit: FormikHandlers['handleSubmit'];
  validateField: FormikHelpers<T>['validateField'];
  isValid: FormikComputedProps<T>['isValid'];
  setFieldValue: FormikHelpers<T>['setFieldValue'];
  setFieldTouched: FormikHelpers<T>['setFieldTouched'];
  values: FormikState<T>['values'];
  errors: FormikState<T>['errors'];

  headers: HeaderEntry[];
  setHeaders: React.Dispatch<React.SetStateAction<HeaderEntry[]>>;
  schemaUploaded: boolean;
  setSchemaUploaded: React.Dispatch<React.SetStateAction<boolean>>;
  advancedOptionsDrawerOpen: boolean;
  setAdvancedOptionsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  advancedOptionsTab: 'headers' | 'schemaFile' | 'localIntrospection';
  setAdvancedOptionsTab: React.Dispatch<
    React.SetStateAction<'headers' | 'schemaFile' | 'localIntrospection'>
  >;
  isUsingLocalIntrospection: boolean;
  setIsUsingLocalIntrospection: React.Dispatch<React.SetStateAction<boolean>>;
  withSubgraphName: boolean;
  accountId?: string | null;
}

// all form controls but formik props are passed in
export const AddSubgraphForm = <T extends AddSubgraphFormFields>({
  graphRef,
  checkSchemaData,
  subgraphNameAvailable,
  shouldShowCompositionErrorsBanner: shouldShowErrorsBanner,
  urlPlaceholder,
  values,
  errors,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  touched,
  setValues,
  validateField,
  headers,
  setHeaders,
  schemaUploaded,
  setSchemaUploaded,
  advancedOptionsDrawerOpen,
  setAdvancedOptionsDrawerOpen,
  advancedOptionsTab,
  setAdvancedOptionsTab,
  isUsingLocalIntrospection,
  setIsUsingLocalIntrospection,
  withSubgraphName,
}: AddSubgraphFormProps<T>) => {
  const compositionErrors =
    checkSchemaData?.service?.checkPartialSchema.compositionValidationResult?.errors.flatMap(
      (error) => error?.message ?? [],
    );

  const stableHeaders = useMemo(
    () => headersObjectFromDefinitions(headers),
    [headers],
  );

  const {
    schema: schemaFromIntrospection,
    subgraphSdl: subgraphSdlFromIntrospection,
    isLoading,
    introspectionFailureType,
    introspectionResponseErrors,
  } = useSchemaFromEndpointIntrospection({
    includeCookies: 'cycle-on-unreachable',
    pollInterval: 3000,
    uri: values.routingUrl,
    stableHeaders,
    skipPolling: !!errors.routingUrl || schemaUploaded,
    pollForSubgraphSdlFirst: true,
    updateOnConfigChange: true,
  });

  const sdlFromIntrospection = useMemo(
    () =>
      subgraphSdlFromIntrospection ??
      (schemaFromIntrospection
        ? printSchema(schemaFromIntrospection)
        : undefined),
    [schemaFromIntrospection, subgraphSdlFromIntrospection],
  );

  useEffect(() => {
    // set the sdl based on endpoint introspection
    // only if user hasn't manually uploaded
    if (!schemaUploaded) {
      if (sdlFromIntrospection && !introspectionFailureType) {
        setFieldValue('sdl', sdlFromIntrospection, true);
      } else {
        setFieldValue('sdl', '');
      }
    }
  }, [
    introspectionFailureType,
    sdlFromIntrospection,
    schemaUploaded,
    setFieldValue,
    values.routingUrl,
  ]);

  // This useEffect is necessary because we programmatically
  // set the subgraphName in the above useEffect & we want to validate the form
  // so the user can click 'next' without focusing & unfocusing the api name field
  useEffect(() => {
    if (values.subgraphName.length > 0 && !touched.subgraphName) {
      validateField('subgraphName');
    }
  }, [touched.subgraphName, validateField, values.subgraphName.length]);

  const endpointIsLocal = useMemo(
    () => isLocalUrl(values.routingUrl),
    [values.routingUrl],
  );

  useEffect(() => {
    if (
      // we show 👀 and open the drawer when folks need to manually upload schema
      introspectionFailureType === 'introspectionDisabled' ||
      // we open the drawer when folks might need to add headers
      introspectionFailureType === 'invalidIntrospectionResponse' ||
      introspectionFailureType === 'invalidIntrospectionSchema' ||
      endpointIsLocal
    ) {
      setAdvancedOptionsDrawerOpen(true);
      setAdvancedOptionsTab('localIntrospection');
    }
  }, [
    endpointIsLocal,
    introspectionFailureType,
    setAdvancedOptionsDrawerOpen,
    setAdvancedOptionsTab,
  ]);

  const introspectionHelper = useMemo(
    () =>
      getIntrospectionHelper({
        schemaUploaded,
        introspectionFailureType,
        isUsingLocalIntrospection,
        isLoading,
        routingUrl: values.routingUrl,
        introspectionResponseErrors,
        sdlFromIntrospection,
        subgraphSdlFromIntrospection,
      }),
    [
      introspectionFailureType,
      introspectionResponseErrors,
      isLoading,
      isUsingLocalIntrospection,
      schemaUploaded,
      sdlFromIntrospection,
      subgraphSdlFromIntrospection,
      values,
    ],
  );

  return (
    <>
      {isMinLengthArray(1, compositionErrors) && shouldShowErrorsBanner && (
        <div className="mb-4">
          <CompositionErrorsBanner
            graphRef={graphRef}
            errors={compositionErrors}
          />
        </div>
      )}
      <FormControl
        isInvalid={
          (touched.routingUrl && !!errors.routingUrl) ||
          introspectionHelper?.type === 'error'
        }
      >
        <URLField
          onBlur={handleBlur}
          autoFocus={false}
          placeholder={urlPlaceholder}
          name="routingUrl"
          onChange={(value) => {
            setValues(
              (fields) => ({ ...fields, routingUrl: value }),
              // validate on change only when there are errors
              'routingUrl' in errors || introspectionHelper?.type === 'error',
            );
            setFieldTouched('routingUrl');
            // on endpoint change, try to prefill the subgraph name as well
            if (withSubgraphName) {
              try {
                const url = new URL(value);
                if (!touched.subgraphName) {
                  const subdomainToFill = url.hostname.split('.')[0];
                  setFieldValue(
                    'subgraphName',
                    decodeURIComponent(subdomainToFill)
                      .replaceAll(/[^a-zA-Z0-9_-]/g, '-')
                      .substring(0, API_NAME_MAX_CHAR),
                  );
                }
              } catch {
                // do nothing, this is just an opportunistic side effect
              }
            }
          }}
          value={values.routingUrl}
          isSocket={false}
        />
        <FormErrorMessage>
          <>
            {introspectionHelper?.type === 'error'
              ? introspectionHelper?.message
              : errors.routingUrl}
          </>
        </FormErrorMessage>
        {introspectionHelper?.type === 'helper' && (
          <FormHelperText>
            {introspectionHelper.icon} {introspectionHelper.message}
          </FormHelperText>
        )}
      </FormControl>
      {withSubgraphName && (
        <FormControl
          isInvalid={
            (errors.subgraphName !== undefined &&
              touched.subgraphName === true) ||
            !subgraphNameAvailable
          }
          className="mt-6"
        >
          <FormLabel>Subgraph name</FormLabel>
          <Input
            onBlur={handleBlur}
            name="subgraphName"
            placeholder="Give your new service a name"
            onChange={(e) => setFieldValue('subgraphName', e.target.value)}
            value={values.subgraphName}
            rightElement={
              <InputCharCount
                count={values.subgraphName.length}
                maxCount={API_NAME_MAX_CHAR}
              />
            }
          />
          <FormErrorMessage>
            <>
              {subgraphNameAvailable
                ? errors.subgraphName
                : `This name is being used by another subgraph. Try a different name or rename your existing subgraph.`}
            </>
          </FormErrorMessage>
        </FormControl>
      )}
      <div className="mt-2">
        <AdvancedOptionsSection
          advancedOptionsDrawerOpen={advancedOptionsDrawerOpen}
          advancedOptionsTab={advancedOptionsTab}
          headers={headers}
          setHeaders={setHeaders}
          setAdvancedOptionsDrawerOpen={setAdvancedOptionsDrawerOpen}
          setAdvancedOptionsTab={setAdvancedOptionsTab}
          setFieldValue={setFieldValue}
          setIsUsingLocalIntrospection={setIsUsingLocalIntrospection}
          setSchemaUploaded={setSchemaUploaded}
          stableHeaders={stableHeaders}
          sdl={values.sdl}
          localEndpointUrl={values.localEndpointUrl}
        />
      </div>
    </>
  );
};
